/**
 * Normalize any common dispatch / GPS shape to { lat, lng } for Google Maps.
 * Returns null if coordinates are not finite numbers in valid ranges.
 * Accepts plain objects, GeoJSON-style points, or [lat, lng] arrays (including string numbers).
 */
export function normalizeMapLatLng(obj) {
  if (!obj) return null;
  if (Array.isArray(obj) && obj.length >= 2) {
    return normalizeMapLatLng({ lat: obj[0], lng: obj[1] });
  }
  if (typeof obj !== 'object') return null;
  const lat = Number(obj.lat ?? obj.latitude ?? obj.Lat ?? obj.Latitude);
  const lng = Number(obj.lng ?? obj.longitude ?? obj.Lon ?? obj.Longitude ?? obj.Lng);
  if (
    !Number.isFinite(lat) ||
    !Number.isFinite(lng) ||
    Math.abs(lat) > 90 ||
    Math.abs(lng) > 180
  ) {
    return null;
  }
  return { lat, lng };
}

/** Server leg object → [lat, lng] for waypoint.location, or [] if unusable. */
export function jobLegToLocationArray(loc) {
  const n = normalizeMapLatLng(loc);
  return n ? [n.lat, n.lng] : [];
}

function isFiniteLatLng(n) {
  return (
    n &&
    Number.isFinite(n.lat) &&
    Number.isFinite(n.lng) &&
    Math.abs(n.lat) <= 90 &&
    Math.abs(n.lng) <= 180
  );
}

/** True if value normalizes to a usable Maps lat/lng (Markers, Map center, etc.). */
export function isValidMapPosition(pos) {
  return isFiniteLatLng(normalizeMapLatLng(pos));
}

/** Accept LatLngLiteral or google.maps.LatLng (lat/lng as number or function). */
export function readGoogleLatLng(value) {
  if (value == null) return null;
  if (typeof value.lat === 'function' && typeof value.lng === 'function') {
    try {
      return normalizeMapLatLng({ lat: value.lat(), lng: value.lng() });
    } catch {
      return null;
    }
  }
  return normalizeMapLatLng(value);
}

/** Plain { lat, lng } for Directions / Markers, or null. */
export function toDirectionsLatLngLiteral(value) {
  const n = readGoogleLatLng(value);
  return isFiniteLatLng(n) ? { lat: n.lat, lng: n.lng } : null;
}

/**
 * Sanitize a DirectionsRequest for @react-google-maps/api (finite literals only).
 * Returns null if origin/destination (or any waypoint) are invalid.
 */
export function sanitizeDirectionsRequest(request) {
  if (!request || typeof window === 'undefined' || !window.google?.maps) return null;
  const origin = toDirectionsLatLngLiteral(request.origin);
  const dest = toDirectionsLatLngLiteral(request.destination);
  if (!origin || !dest) return null;
  const out = {
    origin,
    destination: dest,
    travelMode: request.travelMode ?? window.google.maps.TravelMode.DRIVING,
  };
  if (request.region) out.region = request.region;
  if (request.avoidHighways != null) out.avoidHighways = request.avoidHighways;
  if (request.avoidTolls != null) out.avoidTolls = request.avoidTolls;
  if (request.optimizeWaypoints != null) out.optimizeWaypoints = request.optimizeWaypoints;
  if (Array.isArray(request.waypoints) && request.waypoints.length > 0) {
    const wps = [];
    for (const w of request.waypoints) {
      const loc = toDirectionsLatLngLiteral(w?.location);
      if (!loc) return null;
      wps.push({ location: loc, stopover: w.stopover !== false });
    }
    out.waypoints = wps;
  }
  return out;
}

export function directionsRequestFingerprint(request) {
  const s = sanitizeDirectionsRequest(request);
  if (!s) return '';
  try {
    return JSON.stringify(s);
  } catch {
    return '';
  }
}

/** True if a DirectionsResult is safe to pass to DirectionsRenderer. */
export function isGoogleDirectionsResultRenderable(result) {
  if (!result || result.status !== 'OK') return false;
  const route = result.routes?.[0];
  if (!route?.legs?.length) return false;
  for (const leg of route.legs) {
    if (!leg?.start_location || !leg?.end_location) return false;
    if (!isFiniteLatLng(readGoogleLatLng(leg.start_location))) return false;
    if (!isFiniteLatLng(readGoogleLatLng(leg.end_location))) return false;
  }
  return true;
}

/** Shown when flag / ad-hoc trips have no usable dropoff street from dispatch. */
export const PASSENGER_DETERMINED_DROP = 'Passenger Determined';

function trimLegField(v) {
  if (v == null || v === undefined) return '';
  const s = String(v).trim();
  if (s === '' || s.toLowerCase() === 'undefined' || s.toLowerCase() === 'null') return '';
  return s;
}

/** True when primary line is empty, "0", or numeric-only leading zero (flag-trip placeholders). */
export function isMissingDropoffAddressText(primaryLine) {
  const t = trimLegField(primaryLine);
  if (!t) return true;
  if (t === '0') return true;
  if (/^0(\s+|$)/.test(t) && !/[a-zA-Z]/.test(t)) return true;
  return false;
}

/**
 * Build address1/address2 for a job leg (pickup or dropoff).
 * Dropoff with no landmark/street uses {@link PASSENGER_DETERMINED_DROP}.
 */
export function waypointAddressFromLeg(leg, { isDropoff = false } = {}) {
  if (!leg) {
    return isDropoff
      ? { address1: PASSENGER_DETERMINED_DROP, address2: '' }
      : { address1: '', address2: '' };
  }
  const landmark = trimLegField(leg.landmark ?? leg.Landmark);
  const streetNum = trimLegField(leg.street_num ?? leg.Street_num);
  const streetName = trimLegField(leg.street_name ?? leg.Street_name);
  const city = trimLegField(leg.city ?? leg.City);
  const streetLine = [streetNum, streetName].filter(Boolean).join(' ').trim();

  if (landmark) {
    return {
      address1: landmark,
      address2: streetLine || city,
    };
  }

  const line1 = streetLine;
  const line2 = city && city !== line1 ? city : '';

  if (isDropoff && isMissingDropoffAddressText(line1)) {
    if (line2 && !isMissingDropoffAddressText(line2)) {
      return { address1: PASSENGER_DETERMINED_DROP, address2: line2 };
    }
    return { address1: PASSENGER_DETERMINED_DROP, address2: '' };
  }

  return {
    address1: line1 || line2 || (isDropoff ? PASSENGER_DETERMINED_DROP : ''),
    address2: line1 ? line2 : '',
  };
}

export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = lat1 * Math.PI/180;
  const φ2 = lat2 * Math.PI/180;
  const Δφ = (lat2-lat1) * Math.PI/180;
  const Δλ = (lon2-lon1) * Math.PI/180;

  const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
          Math.cos(φ1) * Math.cos(φ2) *
          Math.sin(Δλ/2) * Math.sin(Δλ/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

  return R * c; // returns distance in meters
};
